import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Clock, XCircle, Eye, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

function SupportModeBanner() {
  const { user, isAuthenticated } = useAuth();
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const fetchActiveSessions = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'admin') {
      setActiveSessions([]);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/support/sessions/active');
      setActiveSessions(response.data.sessions || []);
    } catch (err) {
      // Silently fail - banner is non-critical
      console.debug('Could not fetch support sessions:', err);
      setActiveSessions([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchActiveSessions();

    // Poll for active sessions every 30 seconds
    const interval = setInterval(fetchActiveSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveSessions]);

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Are you sure you want to immediately end this support session?')) {
      return;
    }

    try {
      setRevoking(true);
      await api.post(`/support/sessions/${sessionId}/revoke`, {
        reason: 'Revoked from banner'
      });
      await fetchActiveSessions();
    } catch (err) {
      console.error('Failed to revoke session:', err);
      alert('Failed to revoke support session. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Don't show anything if not admin, loading, or no active sessions
  if (!isAuthenticated || user?.role !== 'admin' || loading || activeSessions.length === 0) {
    return null;
  }

  const session = activeSessions[0]; // Primary session to display

  return (
    <>
      {/* Fixed Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg z-40 relative">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-purple-200" />
              <span className="font-medium">IT SUPPORT MODE ACTIVE</span>
              <span className="mx-3 text-purple-300">|</span>
              <span className="text-purple-100 text-sm">
                {session.support_user_name || session.support_user_id} is viewing your system
              </span>
              <span className="mx-3 text-purple-300">|</span>
              <span className="flex items-center text-purple-200 text-sm">
                <Clock size={14} className="mr-1" />
                {formatTimeRemaining(session.expires_at)} remaining
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-3 py-1 text-sm bg-purple-500/30 hover:bg-purple-500/50 rounded transition flex items-center"
              >
                <Eye size={14} className="mr-1" />
                Details
              </button>
              <button
                onClick={() => handleRevoke(session.id)}
                disabled={revoking}
                className="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 rounded transition flex items-center disabled:opacity-50"
              >
                <XCircle size={14} className="mr-1" />
                End Session
              </button>
            </div>
          </div>

          {/* Animated privacy indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-400/30">
            <div className="h-full bg-purple-300 animate-pulse" style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      {/* Details Panel */}
      {showDetails && (
        <div className="bg-purple-900/95 text-white border-b border-purple-700 shadow-lg z-39">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center text-purple-200">
                  <AlertTriangle size={16} className="mr-2 text-yellow-400" />
                  <span className="text-sm">
                    All client data (names, contact info, case details) is automatically masked during support sessions.
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div>
                    <span className="text-purple-400">Support Staff:</span>
                    <span className="ml-2 text-white">{session.support_user_name || session.support_user_id}</span>
                  </div>
                  <div>
                    <span className="text-purple-400">Email:</span>
                    <span className="ml-2 text-white">{session.support_user_email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-purple-400">Access Level:</span>
                    <span className="ml-2 text-white capitalize">{session.access_level || 'Standard'}</span>
                  </div>
                  <div>
                    <span className="text-purple-400">Financials:</span>
                    <span className="ml-2 text-white">{session.mask_financials ? 'Masked' : 'Visible'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-purple-400">Reason:</span>
                    <span className="ml-2 text-white">{session.request_reason}</span>
                  </div>
                  {session.ticket_id && (
                    <div className="col-span-2">
                      <span className="text-purple-400">Ticket:</span>
                      <span className="ml-2 text-white">{session.ticket_id}</span>
                    </div>
                  )}
                </div>

                {activeSessions.length > 1 && (
                  <div className="mt-3 text-sm text-yellow-300">
                    + {activeSessions.length - 1} additional active session(s)
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowDetails(false)}
                className="text-purple-300 hover:text-white"
              >
                <XCircle size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SupportModeBanner;
